import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import TrendPreviewRunResultsPanel from '../../components/TrendPreviewRunResultsPanel';
import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';
import PostRunNextStepsPanel from '../components/PostRunNextStepsPanel';
import type {
  TrendCategoryRunMode,
  TrendPipelineRunInputMode,
  TrendPipelineRunProfile,
} from '../../types';
import { useTrendAdmin } from '../TrendAdminContext';
import { sectionTitle } from '../adminUi';
import TrendEmptyState from '../../components/TrendEmptyState';
import {
  RunStatusIcon,
  formatRelativeRunUpdate,
  formatRunStep,
  getDiscoveryQueriesForPreset,
  getPipelineRunModeChipLabel,
  getPlanCostChipLabel,
  getPlanModeChipLabel,
  getRunCategoryModeLabel,
  getRunInputModeLabel,
  getRunProfileLabel,
  getRunScopeLabel,
  getSourceTestSources,
} from '../adminHelpers';

export default function StatusTab() {
  const {
    busyAction,
    canReview,
    canManageSources,
    navigateToView,
    hasActiveRun,
    handleBackfillImages,
    setArchiveConfirmOpen,
    handleRunNow,
    derivedRunScope,
    runProfile,
    setRunProfile,
    effectiveRunInputMode,
    setRunInputMode,
    effectiveCategoryRunMode,
    setRunCategoryRunMode,
    runFixtureKey,
    setRunFixtureKey,
    testSets,
    testSetsError,
    selectedTestSet,
    runSourceItemIdsDraft,
    setRunSourceItemIdsDraft,
    latestRun,
    latestRunUpdatedLabel,
    latestRunHeartbeatLabel,
    latestRunPhaseStartedLabel,
    latestRunSlowButAlive,
    latestRunLooksStuck,
    latestRunUsesLegacyTracking,
    handleCancelRun,
    pilotPresetError,
    pilotPresets,
    getPilotPresetStartBlocker,
    sources,
    pilotSourceTestSourceId,
    setPilotSourceTestSourceId,
    discoveryQueries,
    pilotDiscoveryQueryId,
    setPilotDiscoveryQueryId,
    handlePlanPilotRun,
    handleStartPilotRun,
    pilotPlan,
    runs,
    setActiveTab,
    activeRun,
  } = useTrendAdmin();

  const progressRun = activeRun || latestRun;

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Box>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.2rem', fontWeight: 700, color: TREND_COLORS.navy, mb: 0.5 }}>
              Starta trendkörning
            </Typography>
            <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary }}>
              Kör en full trendspaning live, eller välj minikörning/mikrotest för snabbare testkörningar. Full körning kan också begränsas till tydliga kategorispår för billigare och smalare körningar.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0 }}>
            <Button
              variant="outlined"
              disabled={busyAction === 'imageBackfill' || hasActiveRun}
              onClick={() => void handleBackfillImages()}
            >
              {busyAction === 'imageBackfill' ? 'Hämtar bilder...' : 'Återfyll artikelbilder'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              disabled={busyAction === 'archive'}
              onClick={() => setArchiveConfirmOpen(true)}
            >
              Arkivera gamla artiklar
            </Button>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={busyAction === 'run' || busyAction === 'cancel' || busyAction === 'imageBackfill' || hasActiveRun}
              onClick={handleRunNow}
              sx={{
                bgcolor: TREND_COLORS.navy,
                px: 4,
                py: 1.2,
                fontWeight: 700,
                fontSize: '0.9rem',
                letterSpacing: '0.06em',
                '&:hover': { bgcolor: TREND_COLORS.navyLight },
                flexShrink: 0,
              }}
            >
              {hasActiveRun ? 'Körning pågår...' : derivedRunScope === 'preview' ? 'Kör test' : 'Kör nu'}
            </Button>
          </Stack>
        </Stack>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Körprofil"
              value={runProfile}
              onChange={(event) => setRunProfile(event.target.value as TrendPipelineRunProfile)}
              helperText="Full körning kör live. Minikörning och mikrotest körs i testläge."
            >
              <MenuItem value="full">Full körning</MenuItem>
              <MenuItem value="mini">Minikörning</MenuItem>
              <MenuItem value="micro">Mikrotest</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Underlag"
              value={effectiveRunInputMode}
              disabled={runProfile === 'full'}
              onChange={(event) => setRunInputMode(event.target.value as TrendPipelineRunInputMode)}
              helperText={runProfile === 'full'
                ? 'Full körning använder alltid livekällor.'
                : 'Testläget kör riktig artikelanalys för testdata och valda källposter, utan att skriva liveartiklar.'}
            >
              <MenuItem value="fixture">Testdata</MenuItem>
              <MenuItem value="source_items">Valda källposter</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Körläge"
              value={effectiveCategoryRunMode}
              disabled={derivedRunScope !== 'live'}
              onChange={(event) => setRunCategoryRunMode(event.target.value as TrendCategoryRunMode)}
              helperText={derivedRunScope === 'live'
                ? 'Begränsa full körning till en kategoriuppsättning för lägre kostnad och snabbare genomkörning.'
                : 'Kategorikörningar gäller bara live/full körning. Testläge använder alltid vald testdata eller valda källposter.'}
            >
              <MenuItem value="full">Full körning</MenuItem>
              <MenuItem value="design_packaging">Design & förpackning</MenuItem>
              <MenuItem value="food_recipe">Mat & recept</MenuItem>
              <MenuItem value="market_competition">Marknad & konkurrens</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ p: 1.5, minHeight: 82, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
              <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mb: 0.75 }}>
                Aktiv körning
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={derivedRunScope === 'preview' ? 'Testläge' : 'Live'} />
                <Chip size="small" label={getRunProfileLabel(runProfile)} />
                <Chip size="small" label={getRunInputModeLabel(effectiveRunInputMode)} />
                <Chip size="small" label={getRunCategoryModeLabel(effectiveCategoryRunMode)} />
              </Stack>
            </Box>
          </Grid>
          {derivedRunScope === 'live' && effectiveCategoryRunMode !== 'full' && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Den här körningen begränsas till <strong>{getRunCategoryModeLabel(effectiveCategoryRunMode).toLowerCase()}</strong>.
                Endast matchande källor, discovery-frågor, researchunderlag och körningsspecifik rapport används i körningen.
              </Alert>
            </Grid>
          )}
          {runProfile !== 'full' && effectiveRunInputMode === 'fixture' && (
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                fullWidth
                label="Testdata"
                value={runFixtureKey}
                onChange={(event) => setRunFixtureKey(event.target.value)}
                helperText={testSets.length > 0
                  ? 'Testdata ger stabila och återupprepbara testkörningar.'
                  : testSetsError
                    ? 'Testdata-listan kunde inte laddas.'
                    : 'Ingen testdata hittades från backend just nu.'}
              >
                <MenuItem value="" disabled>Välj testdata</MenuItem>
                {testSets.map((testSet) => (
                  <MenuItem key={testSet.key} value={testSet.key}>
                    {testSet.label} ({testSet.itemCount})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {runProfile !== 'full' && effectiveRunInputMode === 'fixture' && testSets.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Alert severity={testSetsError ? 'error' : 'warning'}>
                {testSetsError
                  ? testSetsError
                  : 'Ingen testdata kom tillbaka från backend. Om du nyss uppdaterat trendkoden: starta om backend och ladda om sidan.'}
              </Alert>
            </Grid>
          )}
          {runProfile !== 'full' && effectiveRunInputMode === 'fixture' && selectedTestSet && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>
                  {selectedTestSet.label}
                </Typography>
                <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.5 }}>
                  {selectedTestSet.description || 'Ingen beskrivning tillgänglig.'}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Chip size="small" label={`${selectedTestSet.itemCount} källposter`} />
                <Chip size="small" label={selectedTestSet.recommendedProfile === 'mini' ? 'Rekommenderad: minikörning' : 'Rekommenderad: mikrotest'} />
                  {selectedTestSet.categories.map((category) => (
                    <Chip key={category} size="small" label={category} />
                  ))}
                  {selectedTestSet.scenarioTags.map((tag) => (
                    <Chip key={tag} size="small" variant="outlined" label={tag} />
                  ))}
                </Stack>
              </Box>
            </Grid>
          )}
          {runProfile !== 'full' && effectiveRunInputMode === 'source_items' && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Källpost-ID:n"
                value={runSourceItemIdsDraft}
                onChange={(event) => setRunSourceItemIdsDraft(event.target.value)}
                helperText="Skriv ett id per rad eller kommaseparerat. Testkörningen analyserar dessa källposter utan att skriva liveartiklar."
              />
            </Grid>
          )}
        </Grid>
        {derivedRunScope === 'preview' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Testkörningar påverkar inte livedata. De kör riktig artikelanalys mot testdata eller valda källposter och sammanfattar vilka utkast som klarar kvalitetskraven.
          </Alert>
        )}
        <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: TREND_COLORS.textMuted }}>
          Kör bildåterfyllning först om äldre trendartiklar redan saknar huvudbild. Den försöker läsa om källposter, fylla artiklar och uppdatera berättelser innan nästa fulla körning.
        </Typography>
        {hasActiveRun && progressRun && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="indeterminate"
              sx={{ mb: 1.5, borderRadius: 1, bgcolor: TREND_COLORS.softNavy, '& .MuiLinearProgress-bar': { bgcolor: TREND_COLORS.navy } }}
            />
            <Typography variant="body2" sx={{ color: TREND_COLORS.navy, fontWeight: 600 }}>
              Steg: {formatRunStep(progressRun)}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Chip size="small" label={getRunScopeLabel(progressRun)} />
              <Chip size="small" label={getRunProfileLabel(progressRun.profile)} />
              <Chip size="small" label={getRunInputModeLabel(progressRun.inputMode)} />
              {progressRun.fixtureKey && <Chip size="small" label={`Testdata: ${progressRun.fixtureKey}`} />}
            </Stack>
            <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
              Hämtade: {progressRun.counters.fetched} · Normaliserade: {progressRun.counters.normalized} · Artiklar: {progressRun.counters.analyzed} · Trendtecken: {progressRun.counters.signals} · Möjligheter: {progressRun.counters.opportunities}
            </Typography>
            {progressRun.resultSummary?.headline && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: TREND_COLORS.textMuted }}>
                Sammanfattning: {progressRun.resultSummary.headline}
              </Typography>
            )}
            {progressRun.resultSummary && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {typeof progressRun.resultSummary.testedItems === 'number' && (
                  <Chip size="small" label={`Testade: ${progressRun.resultSummary.testedItems}`} />
                )}
                {typeof progressRun.resultSummary.publishableItems === 'number' && (
                  <Chip size="small" color="success" label={`Publicerbara: ${progressRun.resultSummary.publishableItems}`} />
                )}
                {typeof progressRun.resultSummary.blockedItems === 'number' && (
                  <Chip size="small" color="warning" label={`Blockerade: ${progressRun.resultSummary.blockedItems}`} />
                )}
                {typeof progressRun.resultSummary.ignoredItems === 'number' && (
                  <Chip size="small" label={`Ignorerade: ${progressRun.resultSummary.ignoredItems}`} />
                )}
                {typeof progressRun.resultSummary.reviewItems === 'number' && (
                  <Chip size="small" label={`För granskning: ${progressRun.resultSummary.reviewItems}`} />
                )}
              </Stack>
            )}
            {progressRun.notes.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                {progressRun.notes.slice(0, 6).map((note) => (
                  <Typography key={note} variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                    {note}
                  </Typography>
                ))}
              </Stack>
            )}
            {latestRunUpdatedLabel && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: TREND_COLORS.textMuted }}>
                Senaste framsteg: {latestRunUpdatedLabel}
              </Typography>
            )}
            {latestRunHeartbeatLabel && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: TREND_COLORS.textMuted }}>
                Senaste livstecken: {latestRunHeartbeatLabel}
              </Typography>
            )}
            {latestRunPhaseStartedLabel && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: TREND_COLORS.textMuted }}>
                Nuvarande steg startade: {latestRunPhaseStartedLabel}
              </Typography>
            )}
            {latestRunSlowButAlive && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Backend skickar fortfarande livstecken, men det här steget har inte rapporterat nytt delresultat på ett tag. Det kan vara normalt under långa AI-steg.
              </Alert>
            )}
            {latestRunLooksStuck && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {latestRunUsesLegacyTracking
                  ? 'Körningen har inte rapporterat nytt framsteg på över 15 minuter och den här körningen saknar livsteckenstöd. Kontrollera serverloggen innan du startar om backend.'
                  : 'Inga livstecken från backend på över 5 minuter. Körningen kan ha fastnat eller så har backend startat om. Kontrollera serverloggen innan du startar om körningen.'}
              </Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button
                variant="outlined"
                color="warning"
                disabled={busyAction === 'cancel' || progressRun.status === 'cancelling'}
                onClick={() => void handleCancelRun()}
              >
                {progressRun.status === 'cancelling' ? 'Avbryter...' : 'Avbryt körning'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>

      <PostRunNextStepsPanel
        run={latestRun}
        canReview={canReview}
        canManageSources={canManageSources}
        onControlCenterTab={setActiveTab}
        onNavigateModule={navigateToView}
      />

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        {sectionTitle('Fontana Intelligence Center körmatris', 'Små, säkra körningar enligt implementationsplanen: kategorier, källtest, endast rapport, prompttest och modelltest utan full pipeline.')}
        <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mb: 2 }}>
          Dessa körningar använder körplanering, test-/utkastläge och maxgränser. De är till för att testa underlag utan att behöva köra hela dyra flödet.
        </Typography>
        {pilotPresetError && <Alert severity="warning" sx={{ mb: 2 }}>{pilotPresetError}</Alert>}
        {pilotPresets.length === 0 && !pilotPresetError ? (
          <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, fontStyle: 'italic' }}>
            Inga förvalda testkörningar tillgängliga ännu.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {pilotPresets.map((preset) => {
              const startBlocker = getPilotPresetStartBlocker(preset);
              const sourceOptions = preset.key === 'single_source_test' ? getSourceTestSources(sources) : [];
              const selectedSourceValue = sourceOptions.some((source) => source._id === pilotSourceTestSourceId)
                ? pilotSourceTestSourceId
                : '';
              const discoveryOptions = preset.key === 'single_discovery_query_preview'
                ? getDiscoveryQueriesForPreset(preset, discoveryQueries)
                : [];
              const selectedDiscoveryValue = discoveryOptions.some((query) => query._id === pilotDiscoveryQueryId)
                ? pilotDiscoveryQueryId
                : '';
              return (
                <Grid key={preset.key} size={{ xs: 12, md: 4 }}>
                  <Box sx={{ height: '100%', p: 2, borderRadius: 2, bgcolor: TREND_COLORS.sectionBg, border: `1px solid ${TREND_COLORS.divider}` }}>
                    <Stack spacing={1.25} sx={{ height: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: TREND_COLORS.navy }}>
                        {preset.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, flex: 1 }}>
                        {preset.description}
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={preset.plan.publishMode === 'preview' ? 'Testläge' : preset.plan.publishMode === 'draft' ? 'Utkast' : 'Publicerad'} />
                        <Chip size="small" label={preset.plan.aiProfile === 'preview' ? 'Snabb AI-profil' : preset.plan.aiProfile} />
                        <Chip size="small" label={getPlanModeChipLabel(preset.plan)} />
                        <Chip size="small" label={getPlanCostChipLabel(preset.plan)} />
                      </Stack>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                        Spår: {preset.plan.scope.lanes.join(', ') || 'inga'} · Artiklar: max {preset.plan.limits.maxArticlesToAnalyze}
                      </Typography>
                      {preset.key === 'single_source_test' && (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Källa att testa"
                          value={selectedSourceValue}
                          onChange={(event) => setPilotSourceTestSourceId(event.target.value)}
                          helperText="Välj exakt vilken aktiv källa testet ska hämta från."
                          disabled={busyAction === 'pilotStart' || hasActiveRun}
                        >
                          <MenuItem value="">Välj källa</MenuItem>
                          {sourceOptions.map((source) => (
                            <MenuItem key={source._id} value={source._id}>
                              {source.name} · {source.category}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {preset.key === 'single_discovery_query_preview' && (
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Discovery-fråga att testa"
                          value={selectedDiscoveryValue}
                          onChange={(event) => setPilotDiscoveryQueryId(event.target.value)}
                          helperText="Välj exakt vilken aktiv discovery-fråga AI-webbsöket ska testa."
                          disabled={busyAction === 'pilotStart' || hasActiveRun}
                        >
                          <MenuItem value="">Välj discovery-fråga</MenuItem>
                          {discoveryOptions.map((query) => (
                            <MenuItem key={query._id} value={query._id}>
                              {query.label} · {query.category} · {query.track}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                      {startBlocker && (
                        <Alert severity="info" sx={{ py: 0.5 }}>
                          {startBlocker}
                        </Alert>
                      )}
                      {preset.plan.warnings.length > 0 && (
                        <Alert severity="warning" sx={{ py: 0.5 }}>
                          {preset.plan.warnings[0]}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busyAction === 'pilotPlan' || hasActiveRun}
                          onClick={() => void handlePlanPilotRun(preset)}
                        >
                          Planera
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busyAction === 'pilotStart' || hasActiveRun || !!startBlocker}
                          onClick={() => void handleStartPilotRun(preset)}
                          sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
                        >
                          {preset.plan.publishMode === 'draft' ? 'Starta utkast' : 'Starta test'}
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
        {pilotPlan && (
          <Alert severity={pilotPlan.canStart ? 'info' : 'warning'} sx={{ mt: 2 }}>
            Senaste plan: {pilotPlan.runMode} · {getRunCategoryModeLabel(pilotPlan.categoryRunMode)} · kostnadstak cirka {pilotPlan.estimatedCost.max} {pilotPlan.estimatedCost.currency}.
            {pilotPlan.warnings.length > 0 ? ` Varning: ${pilotPlan.warnings.join(' ')}` : ''}
          </Alert>
        )}
      </Paper>

      <Accordion
        defaultExpanded={false}
        disableGutters
        sx={{
          bgcolor: TREND_COLORS.cardBg,
          border: `1px solid ${TREND_COLORS.divider}`,
          borderRadius: 3,
          boxShadow: 'none',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ width: '100%' }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, color: TREND_COLORS.navy }}>
              Visa senaste körningar
            </Typography>
            <Chip size="small" label={`${runs.length} sparade`} />
            {latestRun && <Chip size="small" variant="outlined" label={`Senast: ${latestRun.status}`} />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {sectionTitle('Körningshistorik', 'De senaste manuella eller schemalagda pipelinekörningarna. Status visar var i flödet körningen befinner sig. Detta är driftloggar – inte att förväxla med fliken Historik, som visar hur trender utvecklas över tid.')}
          {runs.length === 0 ? (
            <TrendEmptyState
              dense
              title="Ingen körning ännu"
              description="Här loggas manuella och schemalagda pipelinekörningar så fort den första har startats."
            />
          ) : (
            <Stack spacing={1}>
              {runs.map((run) => (
                <Stack
                  key={run._id}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: run.status === 'failed' ? '#FFF5F5' : TREND_COLORS.sectionBg,
                    border: `1px solid ${run.status === 'failed' ? '#FED7D7' : TREND_COLORS.divider}`,
                    borderLeft: `3px solid ${run.status === 'failed' ? '#d32f2f' : run.status === 'completed' ? '#2e7d32' : run.status === 'running' || run.status === 'cancelling' ? TREND_COLORS.navy : TREND_COLORS.divider}`,
                  }}
                >
                  <RunStatusIcon status={run.status} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {run.trigger === 'manual' ? 'Manuell' : run.trigger === 'scheduled' ? 'Schemalagd' : 'Rapportgenerering'} · {run.status}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      <Chip size="small" label={getRunScopeLabel(run)} />
                      <Chip size="small" label={getRunProfileLabel(run.profile)} />
                      <Chip size="small" label={getRunInputModeLabel(run.inputMode)} />
                      <Chip size="small" label={getPipelineRunModeChipLabel(run)} />
                      {run.fixtureKey && <Chip size="small" label={`Testdata: ${run.fixtureKey}`} />}
                    </Stack>
                    <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted }}>
                      {run.startedAt?.slice(0, 19).replace('T', ' ')} · {formatRunStep(run)}
                    </Typography>
                    {run.resultSummary?.headline && (
                      <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted }}>
                        {run.resultSummary.headline}
                      </Typography>
                    )}
                    {run.resultSummary && (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {typeof run.resultSummary.testedItems === 'number' && (
                          <Chip size="small" label={`Testade ${run.resultSummary.testedItems}`} />
                        )}
                        {typeof run.resultSummary.publishableItems === 'number' && (
                          <Chip size="small" color="success" label={`Publicerbara ${run.resultSummary.publishableItems}`} />
                        )}
                        {typeof run.resultSummary.blockedItems === 'number' && (
                          <Chip size="small" color="warning" label={`Blockerade ${run.resultSummary.blockedItems}`} />
                        )}
                        {typeof run.resultSummary.ignoredItems === 'number' && (
                          <Chip size="small" label={`Ignorerade ${run.resultSummary.ignoredItems}`} />
                        )}
                        {typeof run.resultSummary.reviewItems === 'number' && (
                          <Chip size="small" label={`För granskning ${run.resultSummary.reviewItems}`} />
                        )}
                      </Stack>
                    )}
                    {run.notes.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted, mt: 0.5 }}>
                        {run.notes[0]}
                      </Typography>
                    )}
                    {formatRelativeRunUpdate(run.updatedAt) && (
                      <Typography variant="caption" sx={{ display: 'block', color: TREND_COLORS.textMuted }}>
                        {formatRelativeRunUpdate(run.updatedAt)}
                      </Typography>
                    )}
                  </Box>
                  {run.status === 'failed' && run.error && (
                    <Tooltip title={run.error}>
                      <IconButton size="small"><ErrorOutlineIcon sx={{ color: '#C53030', fontSize: 18 }} /></IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </AccordionDetails>
      </Accordion>

      <TrendPreviewRunResultsPanel runs={runs} testSets={testSets} />
    </Stack>
  );
}
